import Immutable from 'seamless-immutable';
import { createReducer } from 'redux-act';

import Actions from '../actions';

const initialState = Immutable(
  //the state of the email template designer
  {
    showTokenWarning:     false,
    showTestEmailMessage: false,
    isModalVisible:       false,
    tabIndex:             'SETTINGS',
    token:                {
      required: [],
      known:    [],
      unknown:  [],
      list:     [],
    },
    preview: {
      emailTo:            '',
      isSendingTestEmail: false,
    },
    originalEmailTemplate: {},
    emailTemplate:         {
      name:           '',
      id:             0,
      status:         '',
      instanceConfig: {
        from:         '',
        //these 2 are for internal use ONLY, they will merge and form the 'from' property
        _fromDomain:  '',
        _fromAddress: '',
        to:           '',
        replyTo:      '',
        subject:      '',
        type:         '',
        body:         '',
      },
    },
    isMessageDisplayed: false,
    isFormValid:        true,
    formErrors:         [],
  }
);

const buildErrors = (obj, k = undefined) => {
  const values = [];
  _.forEach(obj, (value, key) => {
    if (_.isObject(value)) {
      values.push(...buildErrors(value, key));
    } else {
      values.push(`${k}`);
    }
  });
  return values;
};

const emailTemplateDesignerReducer = createReducer((on) => {
  on(Actions.hideValidationMessage, (state) =>
    state.merge({
      isFormValid: true,
      formErrors:  [],
    })
  );

  on(Actions.showValidationMessage, (state, { formErrors }) => {
    return state.merge({
      formErrors:  buildErrors(formErrors),
      isFormValid: false,
    });
  });
  //When sending a test email from the Test tab in emailTemplateDesigner, we set the "send" button to inactive for the
  //duration of the sending process. Once that period has passed and we received either a success or fail message, we
  //re-enable the button.
  on(Actions.closeTestEmailMessage, (state) => state.set('showTestEmailMessage', false));

  on(Actions.sendPreviewEmailTemplateSuccess, (state) =>
    state.merge(
      {
        showTestEmailMessage: true,
        preview:              {
          isSendingTestEmail: false,
        },
      },
      { deep: true }
    )
  );

  on(Actions.sendPreviewEmailTemplateFail, (state) =>
    state.merge(
      {
        preview: {
          isSendingTestEmail: false,
        },
      },
      { deep: true }
    )
  );

  on(Actions.sendingPreviewEmailTemplate, (state) => {
    return state.merge(
      {
        preview: {
          isSendingTestEmail: true,
        },
      },
      { deep: true }
    );
  });

  on(Actions.updateNotificationStatusFromList, (state) =>
    state.merge(
      {
        isModalVisible:   false,
        showTokenWarning: false,
      },
      { deep: true }
    )
  );

  //when opening the email template designer we load in an email template and we also add some new keys/values
  //that will helps us better track the "from" and "replyTo" values as they have an optional "Friendly" value and
  //the from value has a limited number of pre-defined domains
  on(Actions.openEmailTemplateDesigner, (state, { emailTemplate }) => {
    //once all details are set and done we do a deep merge to have the value tracked as part of the state tree
    return state.merge(
      {
        isModalVisible:        true,
        emailTemplate,
        //this is a double track so that we can track if the form is dirty to let the user know that changes happened
        //and will require a save to properly track
        originalEmailTemplate: emailTemplate,
        tabIndex:              'SETTINGS',
        isMessageDisplayed:    false,
        showTokenWarning:      false,
        preview:               {
          emailTo: '',
        },
      },
      { deep: true }
    );
  });

  on(Actions.tryCloseEmailTemplateDesigner, (state) => {
    //selecting the original template(un-editable one) and the matching one in the email template designer (his edited
    //brother)
    const originalTemplate = state.originalEmailTemplate;
    const editedTemplate = state.emailTemplate;
    //if the original and the edited template are the same, then there is no reason to pop the message, so we simply
    //close the designer
    if (_.isEqual(originalTemplate, editedTemplate)) {
      return state.merge(
        {
          isModalVisible:     false,
          showTokenWarning:   false,
          isMessageDisplayed: false,
          preview:            {
            emailTo: '',
          },
        },
        { deep: true }
      );
    } else {
      //else we show the message and the user decide if he should proceed to exiting w/o saving changes or if he would
      //like to save current changes then close the designer
      return state.merge(
        {
          isMessageDisplayed: true,
        },
        { deep: true }
      );
    }
  });

  on(Actions.updateEmailTemplateField, (state, { emailTemplate }) => {
    //if we are editing the instance config (subject, body, etc. )
    if (emailTemplate.instanceConfig) {
      // combine the state and emailTemplate instanceConfigs
      const obj = Object.assign({}, state.emailTemplate.instanceConfig, emailTemplate.instanceConfig);

      // destructure the object out into variables
      const { _fromAddress, _fromDomain, _fromFriendly, _replyToFriendly, _replyTo } = obj;

      return state.merge(
        {
          emailTemplate: {
            instanceConfig: {
              ...emailTemplate.instanceConfig,
              _fromAddress:     _fromAddress,
              _fromDomain:      _fromDomain,
              _fromFriendly:    _fromFriendly,
              _replyToFriendly: _replyToFriendly,
              _replyTo:         _replyTo,
            },
          },
        },
        { deep: true }
      );
    } else if (emailTemplate) {
      return state.merge(
        {
          emailTemplate: emailTemplate,
        },
        { deep: true }
      );
      //if none from above then just return state
    } else return state;
  });

  on(Actions.changeDesignerTab, (state, { activeTab }) => state.set('tabIndex', activeTab));

  on(Actions.hideDesignerMessage, (state) => state.set('isMessageDisplayed', false));

  on(Actions.closeEmailTemplateDesigner, (state) => {
    return state.merge(
      {
        isModalVisible:     false,
        showTokenWarning:   false,
        isMessageDisplayed: false,
        preview:            {
          emailTo: '',
        },
      },
      { deep: true }
    );
  });

  on(Actions.setPreviewEmail, (state, { emailTo }) => {
    return state.merge(
      {
        preview: {
          emailTo: emailTo,
        },
      },
      { deep: true }
    );
  });
  on(Actions.checkEmailTemplateTokensSuccess, (state, { unknown, known, required }) => {
    return state.merge(
      {
        showTokenWarning: required.concat(unknown).length !== 0,
        token:            {
          unknown,
          known,
          required,
        },
      },
      { deep: true }
    );
  });

  on(Actions.setCampaignTokenList, (state, { tokenList }) => {
    return state.merge(
      {
        token: {
          list: tokenList,
        },
      },
      { deep: true }
    );
  });

  on(Actions.closeTokenMessage, (state) => {
    return state.merge(
      {
        showTokenWarning: false,
      },
      { deep: true }
    );
  });
}, initialState);

export default emailTemplateDesignerReducer;
